// pages/ListadoLineas.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Table,
    Button,
    Form,
    Select,
    Space,
    Typography,
    message,
    Tag,
    Tooltip,
    Row,
    Col,
    Statistic,
    Card,
    DatePicker,
    Checkbox,
    Radio,
    Tabs,
    Spin,
    Alert,
    Modal,
    Upload,
    Divider,
    Popconfirm,
    Descriptions,
    Progress,
    Input,
    Empty
} from 'antd';
import {
    SearchOutlined,
    ReloadOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    FilePdfOutlined,
    MailOutlined,
    BarChartOutlined,
    TableOutlined,
    SettingOutlined,
    DownloadOutlined,
    EyeOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';
import moment from 'moment';
// import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { debounce } from 'lodash';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

// Constantes para los tipos de cálculo
const CALCULO_TIPOS = [
    { label: 'Habitación', value: 0, icon: '🏠' },
    { label: 'Persona', value: 1, icon: '👤' },
    { label: 'Precio Medio', value: 2, icon: '💰' },
    { label: 'Importe Base', value: 3, icon: '💵' },
    { label: 'Importe Base + IVA', value: 4, icon: '💶' },
    { label: 'Estancias Media Hab.', value: 5, icon: '📅' },
    { label: 'Llegadas', value: 6, icon: '✈️' },
    { label: 'Salidas', value: 7, icon: '🚪' },
];

// Constantes para la agrupación
const AGRUPACION_TIPOS = [
    { label: 'Por Día', value: 0, description: 'Agrupar resultados por día' },
    { label: 'Por Mes', value: 1, description: 'Agrupar resultados por mes' },
    { label: 'Total', value: 2, description: 'Mostrar total general' },
];

const PanelListados = () => {
    // Estados
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const [data, setData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [camposDisponibles, setCamposDisponibles] = useState([]);
    const [viewMode, setViewMode] = useState('table');
    const [tituloReporte, setTituloReporte] = useState('');
    const [metadata, setMetadata] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [chartConfig, setChartConfig] = useState({});
    const [filtros, setFiltros] = useState({
        fechaEstancia: [moment().startOf('day'), moment().add(30, 'days')],
        fechaEntrada: [moment().startOf('day'), moment().startOf('day')],
        tipoCalculo: 0,
        agrupacion: 0,
        incluirCanceladas: false,
        camposAgrupacion: [],
        usarFechasEntrada: false
    });
    
    const [form] = Form.useForm();
    const [emailModalVisible, setEmailModalVisible] = useState(false);
    const [emailForm] = Form.useForm();
    const [exportProgress, setExportProgress] = useState(0);
    
    // Referencias
    const chartRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Cargar campos disponibles al montar el componente
    useEffect(() => {
        cargarCamposDisponibles();
    }, []);

    // Debounce para búsqueda automática
    const debouncedSearch = useCallback(
        debounce(() => {
            if (filtros.camposAgrupacion.length > 0) {
                handleSearch();
            }
        }, 500),
        [filtros]
    );

    // Efecto para búsqueda automática cuando cambian filtros importantes
    useEffect(() => {
        if (filtros.camposAgrupacion.length > 0 && 
            (filtros.tipoCalculo !== undefined || filtros.agrupacion !== undefined)) {
            debouncedSearch();
        }
        return () => debouncedSearch.cancel();
    }, [filtros.tipoCalculo, filtros.agrupacion, filtros.incluirCanceladas, debouncedSearch]);

    // Cargar campos disponibles desde el backend
    const cargarCamposDisponibles = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${BASE_URL}/api/panellistados/campos`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setCamposDisponibles(response.data.data);
                // Por defecto, seleccionar los primeros 3 campos
                const camposPorDefecto = response.data.data.slice(0, 3).map(c => c.id);
                setFiltros(prev => ({ ...prev, camposAgrupacion: camposPorDefecto }));
                form.setFieldsValue({ camposAgrupacion: camposPorDefecto });
            }
        } catch (error) {
            console.error('Error cargando campos:', error);
            message.error('Error al cargar los campos disponibles');
        }
    };

    // Función principal para buscar datos
    const handleSearch = async () => {
        // Cancelar petición anterior si existe
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        // Crear nuevo AbortController
        abortControllerRef.current = new AbortController();

        setLoading(true);
        
        try {
            const token = localStorage.getItem('token');
            
            // Validar que haya al menos un campo seleccionado
            if (filtros.camposAgrupacion.length === 0) {
                message.warning('Seleccione al menos un campo para agrupar');
                setLoading(false);
                return;
            }

            // Construir payload
            const payload = {
                fechaDesde: filtros.fechaEstancia[0]?.format('YYYY-MM-DD'),
                fechaHasta: filtros.fechaEstancia[1]?.format('YYYY-MM-DD'),
                tipoCalculo: filtros.tipoCalculo,
                agrupacion: filtros.agrupacion,
                incluirCanceladas: filtros.incluirCanceladas,
                camposAgrupacion: filtros.camposAgrupacion
            };

            // Agregar fechas de entrada si están habilitadas
            if (filtros.usarFechasEntrada && filtros.fechaEntrada[0] && filtros.fechaEntrada[1]) {
                payload.fechaEntradaDesde = filtros.fechaEntrada[0].format('YYYY-MM-DD');
                payload.fechaEntradaHasta = filtros.fechaEntrada[1].format('YYYY-MM-DD');
            }

            // Realizar la consulta
            const response = await axios.post(
                `${BASE_URL}/api/panellistados/consultar`,
                payload,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    signal: abortControllerRef.current.signal,
                    timeout: 30000 // 30 segundos timeout
                }
            );

            if (response.data.success) {
                setData(response.data.data);
                setColumns(response.data.columnas);
                setMetadata(response.data.metadata);
                
                // Preparar datos para gráfico
                if (response.data.configGrafico) {
                    setChartData(response.data.configGrafico.data);
                    setChartConfig(response.data.configGrafico.config);
                }
                
                construirTitulo();
                
                const tiempoMsg = response.data.metadata?.desdeCache 
                    ? '(desde caché)' 
                    : `(${response.data.metadata?.tiempoProcesamiento}ms)`;
                message.success(`Datos cargados correctamente ${tiempoMsg}`);
            } else {
                message.error(response.data.error || 'Error al cargar los datos');
            }
        } catch (error) {
            if (error.name === 'AbortError') {
                console.log('Petición cancelada');
                return;
            }
            console.error('Error cargando listado:', error);
            message.error(error.response?.data?.error || 'Error al cargar el listado');
        } finally {
            setLoading(false);
            abortControllerRef.current = null;
        }
    };

    // Construir título del reporte
    const construirTitulo = () => {
        const tipoCalculoLabel = CALCULO_TIPOS.find(t => t.value === filtros.tipoCalculo)?.label || 'Habitación';
        const camposLabels = camposDisponibles
            .filter(c => filtros.camposAgrupacion.includes(c.id))
            .map(c => c.descripcion)
            .join(', ');
        
        let titulo = `${tipoCalculoLabel} - Agrupado por: ${camposLabels || 'Ninguno'}`;
        titulo += `\nPeríodo: ${filtros.fechaEstancia[0]?.format('DD/MM/YYYY')} al ${filtros.fechaEstancia[1]?.format('DD/MM/YYYY')}`;
        
        if (filtros.usarFechasEntrada && filtros.fechaEntrada[0]) {
            titulo += `\nEntradas: ${filtros.fechaEntrada[0]?.format('DD/MM/YYYY')} al ${filtros.fechaEntrada[1]?.format('DD/MM/YYYY')}`;
        }
        
        if (filtros.incluirCanceladas) {
            titulo += `\n(Incluye canceladas)`;
        }
        
        setTituloReporte(titulo);
    };

    // Exportar a Excel (procesamiento en cliente)
    const handleExportExcel = async () => {
        if (data.length === 0) {
            message.warning('No hay datos para exportar');
            return;
        }

        setExporting(true);
        setExportProgress(0);

        try {
            // Simular progreso
            const progressInterval = setInterval(() => {
                setExportProgress(prev => Math.min(prev + 10, 90));
            }, 100);

            // Crear libro de Excel
            const worksheet = XLSX.utils.json_to_sheet(data);
            
            // Personalizar ancho de columnas
            const colWidths = columns.map(col => ({
                wch: Math.min(Math.max(col.title.length + 5, 15), 50)
            }));
            worksheet['!cols'] = colWidths;

            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Listado');
            
            // Agregar hoja de metadatos
            const metadataSheet = XLSX.utils.json_to_sheet([
                { Campo: 'Título', Valor: tituloReporte },
                { Campo: 'Fecha generación', Valor: new Date().toLocaleString() },
                { Campo: 'Total registros', Valor: data.length },
                { Campo: 'Tipo cálculo', Valor: CALCULO_TIPOS.find(t => t.value === filtros.tipoCalculo)?.label },
                { Campo: 'Agrupación', Valor: AGRUPACION_TIPOS.find(t => t.value === filtros.agrupacion)?.label },
                { Campo: 'Campos agrupados', Valor: camposDisponibles.filter(c => filtros.camposAgrupacion.includes(c.id)).map(c => c.descripcion).join(', ') }
            ]);
            XLSX.utils.book_append_sheet(workbook, metadataSheet, 'Metadatos');

            clearInterval(progressInterval);
            setExportProgress(100);
            
            // Exportar archivo
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const dataBlob = new Blob([excelBuffer], { type: 'application/octet-stream' });
            const filename = `listado_lineas_${moment().format('YYYYMMDD_HHmmss')}.xlsx`;
            // saveAs(dataBlob, filename);
            
            message.success('Archivo Excel generado correctamente');
        } catch (error) {
            console.error('Error exportando Excel:', error);
            message.error('Error al generar el archivo Excel');
        } finally {
            setTimeout(() => {
                setExporting(false);
                setExportProgress(0);
            }, 500);
        }
    };

    // Exportar a PDF (impresión)
    const handleExportPDF = () => {
        if (data.length === 0) {
            message.warning('No hay datos para exportar');
            return;
        }
        
        // Crear contenido HTML para impresión
        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <title>${tituloReporte}</title>
                <style>
                    body { font-family: Arial, sans-serif; margin: 20px; }
                    h1 { color: #1890ff; border-bottom: 2px solid #1890ff; padding-bottom: 10px; }
                    .metadata { background: #f5f5f5; padding: 15px; margin: 20px 0; border-radius: 5px; }
                    table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; font-weight: bold; }
                    tr:nth-child(even) { background-color: #f9f9f9; }
                    .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
                </style>
            </head>
            <body>
                <h1>${tituloReporte.replace(/\n/g, '<br>')}</h1>
                <div class="metadata">
                    <strong>Metadatos:</strong><br>
                    Fecha generación: ${new Date().toLocaleString()}<br>
                    Total registros: ${data.length}<br>
                    ${metadata?.tiempoProcesamiento ? `Tiempo procesamiento: ${metadata.tiempoProcesamiento}ms<br>` : ''}
                    ${metadata?.desdeCache ? 'Datos desde caché<br>' : ''}
                </div>
                 <table>
                    <thead>
                        <tr>
                            ${columns.map(col => `<th>${col.title}</th>`).join('')}
                        </tr>
                    </thead>
                    <tbody>
                        ${data.map(row => `
                            <tr>
                                ${columns.map(col => `<td>${row[col.dataIndex] !== undefined ? row[col.dataIndex] : '-'}</td>`).join('')}
                            </tr>
                        `).join('')}
                    </tbody>
                 </table>
                <div class="footer">
                    Generado por Sistema Hotelero - ${new Date().toLocaleString()}
                </div>
            </body>
            </html>
        `;
        
        const printWindow = window.open('', '_blank');
        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.print();
    };

    // Enviar por correo
    const handleSendEmail = () => {
        if (data.length === 0) {
            message.warning('No hay datos para enviar');
            return;
        }
        setEmailModalVisible(true);
    };

    // Procesar envío de correo
    const handleEmailSubmit = async (values) => {
        setSendingEmail(true);
        
        try {
            const token = localStorage.getItem('token');
            
            // Preparar datos adjuntos
            const datosAdjuntos = {
                data: data,
                columns: columns,
                titulo: tituloReporte,
                totalRegistros: data.length
            };
            
            // Enviar al backend
            const response = await axios.post(
                `${BASE_URL}/api/panellistados/enviar-correo`,
                {
                    para: values.para,
                    asunto: values.asunto,
                    cuerpo: values.cuerpo,
                    datosAdjuntos: datosAdjuntos
                },
                {
                    headers: { Authorization: `Bearer ${token}` },
                    timeout: 60000 // 60 segundos para envío de correo
                }
            );
            
            if (response.data.success) {
                message.success('Correo enviado correctamente');
                setEmailModalVisible(false);
                emailForm.resetFields();
            } else {
                message.error(response.data.error || 'Error al enviar el correo');
            }
        } catch (error) {
            console.error('Error enviando correo:', error);
            message.error(error.response?.data?.error || 'Error al enviar el correo');
        } finally {
            setSendingEmail(false);
        }
    };

    // Renderizar tabla con optimización
    const renderTable = useMemo(() => {
        if (columns.length === 0) return null;

        const tableColumns = columns.map(col => ({
            title: col.title,
            dataIndex: col.dataIndex,
            key: col.dataIndex,
            render: (text, record) => {
                if (col.dataType === 'number') {
                    const num = parseFloat(text);
                    if (!isNaN(num)) {
                        // Formateo condicional según el valor
                        if (col.title.includes('%') || col.title.includes('Porcentaje')) {
                            return <span>{num.toFixed(2)}%</span>;
                        }
                        if (col.title.includes('Precio') || col.title.includes('Importe')) {
                            return <span>€ {num.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
                        }
                        return num % 1 !== 0 ? num.toFixed(2) : num.toLocaleString();
                    }
                }
                if (col.dataType === 'date' && text) {
                    return moment(text).format('DD/MM/YYYY');
                }
                if (col.dataType === 'boolean') {
                    return text ? 
                        <CheckCircleOutlined style={{ color: '#52c41a' }} /> : 
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
                }
                return text || '-';
            },
            sorter: (a, b) => {
                const valA = a[col.dataIndex];
                const valB = b[col.dataIndex];
                if (col.dataType === 'number') {
                    return (parseFloat(valA) || 0) - (parseFloat(valB) || 0);
                }
                if (col.dataType === 'date') {
                    return moment(valA).unix() - moment(valB).unix();
                }
                return String(valA).localeCompare(String(valB));
            },
            ellipsis: col.dataType === 'string',
            width: col.dataType === 'number' ? 120 : undefined
        }));

        return (
            <Table
                columns={tableColumns}
                dataSource={data}
                rowKey={(record, index) => `${record[columns[0]?.dataIndex]}_${index}`}
                loading={loading}
                scroll={{ x: 'max-content', y: 600 }}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showQuickJumper: true,
                    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
                    pageSizeOptions: ['10', '20', '50', '100']
                }}
                size="middle"
                bordered
                className="listado-table"
            />
        );
    }, [data, columns, loading]);

    // Renderizar gráfico
    const renderChart = () => {
        if (chartData.length === 0) {
            return (
                <Empty 
                    description="No hay datos suficientes para mostrar el gráfico"
                    style={{ marginTop: 50 }}
                />
            );
        }

        // Datos para gráfico simple (para implementación real usar Recharts o Chart.js)
        const maxValue = Math.max(...chartData.map(item => item.value));
        
        return (
            <Card 
                title={chartConfig.title || "Gráfico de Datos"}
                style={{ marginTop: 16 }}
                extra={
                    <Tag color="blue">
                        Total: {chartData.reduce((sum, item) => sum + item.value, 0).toLocaleString()}
                    </Tag>
                }
            >
                <div style={{ overflowX: 'auto', padding: '20px' }}>
                    {chartData.map((item, index) => {
                        const percentage = (item.value / maxValue) * 100;
                        return (
                            <div key={index} style={{ marginBottom: 16 }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                                    <Text strong>{item.name}</Text>
                                    <Text>{item.value.toLocaleString()}</Text>
                                </div>
                                <Progress 
                                    percent={percentage} 
                                    showInfo={false}
                                    strokeColor={{
                                        '0%': '#108ee9',
                                        '100%': '#87d068',
                                    }}
                                />
                            </div>
                        );
                    })}
                </div>
                <Divider />
                <div style={{ textAlign: 'center', color: '#999', fontSize: 12 }}>
                    <InfoCircleOutlined /> Gráfico de barras - {CALCULO_TIPOS.find(t => t.value === filtros.tipoCalculo)?.label}
                </div>
            </Card>
        );
    };

    // Renderizar estadísticas
    const renderStats = () => {
        if (data.length === 0) return null;

        // Encontrar columnas numéricas
        const numColumns = columns.filter(col => col.dataType === 'number');
        const totalPorColumna = {};
        
        numColumns.forEach(col => {
            totalPorColumna[col.title] = data.reduce((sum, item) => {
                const val = parseFloat(item[col.dataIndex]);
                return sum + (isNaN(val) ? 0 : val);
            }, 0);
        });

        return (
            <Row gutter={16} style={{ marginBottom: 24 }}>
                <Col span={6}>
                    <Statistic 
                        title="Total Registros" 
                        value={data.length} 
                        prefix={<TableOutlined />}
                        valueStyle={{ color: '#1890ff' }}
                    />
                </Col>
                {Object.entries(totalPorColumna).slice(0, 3).map(([titulo, total]) => (
                    <Col span={6} key={titulo}>
                        <Statistic 
                            title={titulo} 
                            value={total.toFixed(2)} 
                            precision={2}
                            prefix={<BarChartOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Col>
                ))}
                {metadata?.tiempoProcesamiento && (
                    <Col span={6}>
                        <Statistic 
                            title="Tiempo Procesamiento" 
                            value={metadata.tiempoProcesamiento} 
                            suffix="ms"
                            valueStyle={{ color: metadata.desdeCache ? '#faad14' : '#1890ff' }}
                        />
                        {metadata.desdeCache && <Tag color="gold" style={{ marginTop: 8 }}>Desde caché</Tag>}
                    </Col>
                )}
            </Row>
        );
    };

    // Filtros del reporte
    const filtersPanel = (
        <Card style={{ marginBottom: 24 }}>
            <Form
                form={form}
                layout="vertical"
                initialValues={{
                    fechaEstancia: filtros.fechaEstancia,
                    fechaEntrada: filtros.fechaEntrada,
                    tipoCalculo: filtros.tipoCalculo,
                    agrupacion: filtros.agrupacion,
                    incluirCanceladas: filtros.incluirCanceladas,
                    camposAgrupacion: filtros.camposAgrupacion,
                    usarFechasEntrada: filtros.usarFechasEntrada
                }}
                onValuesChange={(changedValues, allValues) => {
                    setFiltros(prev => ({ ...prev, ...allValues }));
                }}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Form.Item name="fechaEstancia" label="📅 Fechas de Estancia">
                            <RangePicker 
                                style={{ width: '100%' }} 
                                format="DD/MM/YYYY"
                                disabledDate={(current) => current && current > moment().endOf('day').add(2, 'years')}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item name="usarFechasEntrada" valuePropName="checked">
                            <Checkbox>Usar fechas de entrada específicas</Checkbox>
                        </Form.Item>
                        {filtros.usarFechasEntrada && (
                            <Form.Item name="fechaEntrada" label="🚪 Fechas de Entrada">
                                <RangePicker 
                                    style={{ width: '100%' }} 
                                    format="DD/MM/YYYY"
                                    disabledDate={(current) => current && current < moment().startOf('day')}
                                />
                            </Form.Item>
                        )}
                    </Col>
                </Row>
                
                <Row gutter={24}>
                    <Col span={8}>
                        <Form.Item name="tipoCalculo" label="📊 Tipo de Cálculo">
                            <Select>
                                {CALCULO_TIPOS.map(tipo => (
                                    <Option key={tipo.value} value={tipo.value}>
                                        {tipo.icon} {tipo.label}
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item 
                            name="agrupacion" 
                            label="📁 Agrupación"
                            tooltip={AGRUPACION_TIPOS.find(t => t.value === filtros.agrupacion)?.description}
                        >
                            <Select>
                                {AGRUPACION_TIPOS.map(tipo => (
                                    <Option key={tipo.value} value={tipo.value}>{tipo.label}</Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={8}>
                        <Form.Item name="incluirCanceladas" valuePropName="checked">
                            <Checkbox>🗑️ Incluir Reservas Canceladas</Checkbox>
                        </Form.Item>
                    </Col>
                </Row>

                <Row gutter={24}>
                    <Col span={24}>
                        <Form.Item 
                            name="camposAgrupacion" 
                            label="🏷️ Campos para Agrupar"
                            rules={[{ required: true, message: 'Seleccione al menos un campo' }]}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Seleccione los campos para agrupar"
                                style={{ width: '100%' }}
                                showSearch
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                                }
                            >
                                {camposDisponibles.map(campo => (
                                    <Option key={campo.id} value={campo.id}>
                                        {campo.descripcion}
                                        <Tag style={{ marginLeft: 8 }}>{campo.tipo}</Tag>
                                    </Option>
                                ))}
                            </Select>
                        </Form.Item>
                    </Col>
                </Row>

                <Row>
                    <Col span={24}>
                        <Space>
                            <Button 
                                type="primary" 
                                icon={<SearchOutlined />} 
                                onClick={handleSearch} 
                                loading={loading}
                                size="large"
                            >
                                Buscar
                            </Button>
                            <Button 
                                icon={<ReloadOutlined />} 
                                onClick={() => {
                                    form.resetFields();
                                    setFiltros({
                                        fechaEstancia: [moment().startOf('day'), moment().add(30, 'days')],
                                        fechaEntrada: [moment().startOf('day'), moment().startOf('day')],
                                        tipoCalculo: 0,
                                        agrupacion: 0,
                                        incluirCanceladas: false,
                                        camposAgrupacion: camposDisponibles.slice(0, 3).map(c => c.id),
                                        usarFechasEntrada: false
                                    });
                                }}
                                size="large"
                            >
                                Restablecer
                            </Button>
                        </Space>
                    </Col>
                </Row>
            </Form>
        </Card>
    );

    return (
        <PageContainer
            title="Listado de Líneas"
            subtitle={tituloReporte || "Configure los parámetros para generar el listado"}
            icon={<SettingOutlined />}
            loading={loading}
            onRefresh={handleSearch}
            extra={
                <Space>
                    <Tooltip title="Vista Tabla">
                        <Button
                            type={viewMode === 'table' ? 'primary' : 'default'}
                            icon={<TableOutlined />}
                            onClick={() => setViewMode('table')}
                        />
                    </Tooltip>
                    <Tooltip title="Vista Gráfico">
                        <Button
                            type={viewMode === 'chart' ? 'primary' : 'default'}
                            icon={<BarChartOutlined />}
                            onClick={() => setViewMode('chart')}
                            disabled={chartData.length === 0}
                        />
                    </Tooltip>
                    <Tooltip title="Exportar a Excel">
                        <Button 
                            icon={<FileExcelOutlined />} 
                            onClick={handleExportExcel} 
                            disabled={data.length === 0 || exporting}
                            loading={exporting}
                        >
                            {exporting && `${exportProgress}%`}
                        </Button>
                    </Tooltip>
                    <Tooltip title="Exportar a PDF">
                        <Button 
                            icon={<FilePdfOutlined />} 
                            onClick={handleExportPDF} 
                            disabled={data.length === 0}
                        />
                    </Tooltip>
                    <Tooltip title="Enviar por Correo">
                        <Button 
                            icon={<MailOutlined />} 
                            onClick={handleSendEmail} 
                            disabled={data.length === 0 || sendingEmail}
                            loading={sendingEmail}
                        />
                    </Tooltip>
                    <Tooltip title="Imprimir">
                        <Button 
                            icon={<PrinterOutlined />} 
                            onClick={handleExportPDF} 
                            disabled={data.length === 0}
                        />
                    </Tooltip>
                </Space>
            }
            cardStyle={{ borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}
        >
            {filtersPanel}
            {renderStats()}
            {viewMode === 'table' ? renderTable : renderChart()}
            
            {/* Modal para envío de correo */}
            <Modal
                title="Enviar por Correo"
                open={emailModalVisible}
                onCancel={() => setEmailModalVisible(false)}
                footer={null}
                width={600}
            >
                <Form
                    form={emailForm}
                    layout="vertical"
                    onFinish={handleEmailSubmit}
                    initialValues={{
                        asunto: tituloReporte.split('\n')[0] || 'Listado de Líneas',
                        cuerpo: `Se adjunta el listado solicitado.\n\n${tituloReporte}\n\nTotal registros: ${data.length}\n\nSaludos cordiales.`
                    }}
                >
                    <Form.Item
                        name="para"
                        label="Para"
                        rules={[{ required: true, message: 'Ingrese el destinatario', type: 'email' }]}
                    >
                        <Input placeholder="correo@ejemplo.com" />
                    </Form.Item>
                    <Form.Item
                        name="asunto"
                        label="Asunto"
                        rules={[{ required: true, message: 'Ingrese el asunto' }]}
                    >
                        <Input />
                    </Form.Item>
                    <Form.Item
                        name="cuerpo"
                        label="Mensaje"
                        rules={[{ required: true, message: 'Ingrese el mensaje' }]}
                    >
                        <Input.TextArea rows={6} />
                    </Form.Item>
                    <Form.Item>
                        <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                            <Button onClick={() => setEmailModalVisible(false)}>
                                Cancelar
                            </Button>
                            <Button type="primary" htmlType="submit" loading={sendingEmail}>
                                Enviar
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Modal>
        </PageContainer>
    );
};

export default PanelListados;